import React, { useState, useCallback } from 'react';
import { X, Barcode } from 'lucide-react';
import './ISBNModal.css';

interface ISBNModalProps {
  onGenerate: (isbn: string) => void;
  onPlaceholder: () => void;
  onClose: () => void;
}

function parseISBN(raw: string): { digits: string; valid: boolean; error: string } {
  const digits = raw.replace(/[-\s]/g, '');

  if (!digits) return { digits, valid: false, error: '' };
  if (!/^\d+$/.test(digits)) return { digits, valid: false, error: 'Numbers only' };
  if (digits.length < 13) return { digits, valid: false, error: `${digits.length} / 13 digits` };
  if (digits.length > 13) return { digits, valid: false, error: 'Too many digits' };
  if (!digits.startsWith('978') && !digits.startsWith('979'))
    return { digits, valid: false, error: 'Must start with 978 or 979' };

  // EAN-13 checksum
  const sum = digits.slice(0, 12).split('').reduce(
    (acc, d, i) => acc + parseInt(d) * (i % 2 === 0 ? 1 : 3), 0,
  );
  const check = (10 - (sum % 10)) % 10;
  if (check !== parseInt(digits[12]))
    return { digits, valid: false, error: 'Invalid check digit' };

  return { digits, valid: true, error: '' };
}

const ISBNModal: React.FC<ISBNModalProps> = ({ onGenerate, onPlaceholder, onClose }) => {
  const [value, setValue] = useState('');

  const { digits, valid, error } = parseISBN(value);
  const hasInput = digits.length > 0;

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && valid) onGenerate(digits);
    if (e.key === 'Escape') onClose();
  }, [valid, digits, onGenerate, onClose]);

  return (
    <div className="isbn-overlay" onClick={onClose}>
      <div className="isbn-modal" onClick={e => e.stopPropagation()}>

        <div className="isbn-header">
          <div className="isbn-header-icon"><Barcode size={15} strokeWidth={1.75} /></div>
          <span className="isbn-title">Add ISBN Barcode</span>
          <button className="isbn-close" onClick={onClose}><X size={14} strokeWidth={2} /></button>
        </div>

        <div className="isbn-body">
          <p className="isbn-hint">
            Enter your ISBN-13 to generate a real barcode, or place a placeholder
            and replace it later.
          </p>

          <div className="isbn-field">
            <label className="isbn-label">ISBN-13</label>
            <input
              className={`isbn-input${hasInput ? (valid ? ' valid' : ' invalid') : ''}`}
              type="text"
              placeholder="978-0-000000-00-0"
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={17}
              autoFocus
              spellCheck={false}
            />
            {hasInput && (
              <span className={`isbn-feedback ${valid ? 'ok' : 'err'}`}>
                {valid ? '✓ Valid ISBN-13' : error}
              </span>
            )}
          </div>
        </div>

        <div className="isbn-footer">
          <button className="isbn-btn-placeholder" onClick={onPlaceholder}>
            Place Placeholder
          </button>
          <button className="isbn-btn-generate" disabled={!valid} onClick={() => onGenerate(digits)}>
            <Barcode size={13} strokeWidth={1.75} />
            Generate &amp; Place
          </button>
        </div>

      </div>
    </div>
  );
};

export default ISBNModal;
